import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/ui/skeleton';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { BillingCreditsCard } from 'features/credits/components/billing-credits-card';
import { SeatsOverview } from 'features/iam/components/seats/seats-overview';
import { InvoicesTable } from 'features/credits/components/invoices-table';
import { BillingDetailsCard } from 'features/subscription/components/billing-details-card';
import { NextBillCard } from 'features/subscription/components/next-bill-card';
import { PaymentMethodCard } from 'features/subscription/components/payment-method-card';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';

// Hidden for now; set to true to show the payment method card again.
const SHOW_PAYMENT_METHOD = false;

/** Admin-only: the seat bill, top-up credits and the organization's plans. */
export default function PlansBillingPage() {
  const { t } = useTranslation();
  const { planSeats, isLoading } = usePlanSeats();

  return (
    <ConfigurationLayout>
      {/* No org switcher here: the top bar already has one. */}
      <header>
        <h1 className="text-2xl font-medium leading-heading text-grey-950 dark:text-true-white">
          {t('PLANS_AND_BILLING', 'Plans & Billing')}
        </h1>
        <p className="mt-1 text-sm text-grey-500 dark:text-muted-foreground">
          {t(
            'PLANS_AND_BILLING_SUBTITLE',
            'See what you pay, change seats, add credits and download invoices.'
          )}
        </p>
      </header>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-5">
          <Skeleton className="h-72 rounded-section lg:col-span-3" />
          <Skeleton className="h-72 rounded-section lg:col-span-2" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-5">
            <NextBillCard planSeats={planSeats} />
            <BillingCreditsCard />
          </div>
          <SeatsOverview
            planSeats={planSeats}
            title={t('PLANS', 'Plans')}
            description={t('PLANS_SECTION_DESCRIPTION', 'Each seat is for one person. You can mix plans across your team.')}
          />
          {/* Billing details fill the row while the payment method card is hidden. */}
          {SHOW_PAYMENT_METHOD ? (
            <div className="grid gap-4 lg:grid-cols-5">
              <PaymentMethodCard planSeats={planSeats} />
              <BillingDetailsCard />
            </div>
          ) : (
            <BillingDetailsCard />
          )}
          <InvoicesTable />
        </>
      )}
    </ConfigurationLayout>
  );
}
